"use client";

import React from "react";
import { MapPin, Phone, Mail, Send, Loader as Loader2, Clock, Building } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  icon: Building,
  title: "Kantor Pusat",
  details: ["Gedung ABADI.NEWS", "Jl. Jend. Sudirman Kav. 21", "Jakarta Pusat, 10220, Indonesia"]
}, {
  icon: Phone,
  title: "Telepon",
  details: ["+62 21 555 0123 (Redaksi)", "+62 811 9999 8888 (WhatsApp)"]
}, {
  icon: Mail,
  title: "Email",
  details: ["redaksi@abadi.news (Berita)", "kerjasama@abadi.news (Bisnis)"]
}, {
  icon: Clock,
  title: "Jam Operasional",
  details: ["Senin - Jumat: 08:00 - 17:00 WIB", "Sabtu - Minggu: Tutup (Layanan Darurat 24/7)"]
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-16 bg-primary/10 text-primary rounded-lg flex items-center justify-center mb-4">
          <Send className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Pesan Terkirim!</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Kirim Pesan Lainnya
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Nama lengkap wajib diisi",
        minLength: {
          value: 2,
          message: "Minimal 2 karakter"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Nama Lengkap</FormLabel>
              <FormControl>
                <Input placeholder="Masukkan nama Anda" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email wajib diisi",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Format email tidak valid"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Alamat Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="nama@email.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" rules={{
      required: "Subjek wajib diisi"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Subjek</FormLabel>
            <FormControl>
              <Input placeholder="Pertanyaan, Kerjasama, atau Umpan Balik" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Pesan wajib diisi",
      minLength: {
        value: 10,
        message: "Pesan minimal 10 karakter"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Pesan</FormLabel>
            <FormControl>
              <Textarea placeholder="Tuliskan pesan Anda secara detail di sini..." className="min-h-[150px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[160px]">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-4" />
            Mengirim...
          </> : <>
            <Send className="mr-2 size-4" />
            Kirim Pesan
          </>}
      </Button>
    </Form>;
}
export default function ContactInformation() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: infoRef,
    isVisible: isInfoVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact-information" className="w-full bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-20 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            Hubungi <span className="text-primary">ABADI.NEWS</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Kami selalu terbuka untuk mendengar dari Anda. Baik itu pertanyaan, umpan balik, informasi berita, atau peluang kerjasama, tim kami siap membantu.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {}
          <div ref={infoRef} className={`lg:col-span-5 space-y-6 transition-all duration-700 delay-100 ease-out ${isInfoVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
              {contactDetails.map((item, i) => <Card key={i} data-index={i} className="border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 bg-card/50 backdrop-blur-sm">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="shrink-0 p-3 bg-primary/10 rounded-lg text-primary">
                      <item.icon className="size-6" />
                    </div>
                    <div>
                      <h3 className="font-default font-semibold text-foreground text-lg mb-2">
                        {item.title}
                      </h3>
                      <div className="space-y-1">
                        {item.details.map((detail, j) => <p key={j} className="text-muted-foreground text-sm leading-relaxed">
                            {detail}
                          </p>)}
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          {}
          <div ref={formRef} className={`lg:col-span-7 transition-all duration-700 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <Card className="border-border shadow-lg bg-card">
              <CardHeader className="pb-6 border-b border-border/50">
                <CardTitle className="text-2xl font-default">Kirim Pesan Langsung</CardTitle>
                <CardDescription className="text-base">
                  Isi formulir di bawah ini dan tim kami akan merespons secepatnya.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="contact-page-main-form" sectionName="contact-information" defaultValues={{
                name: "",
                email: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                subject: {
                  required: true
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Kirim Pesan" successMessage="Terima kasih telah menghubungi kami. Pesan Anda telah diterima dan akan segera kami proses.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}